import { useSearchParams } from 'react-router-dom';
import { usePremiumFund } from 'entities/salary/salary-entries/salary-entries.hook';
import { Box, Button, FormControl, FormGroup, FormLabel, Stack, TableCell, TableRow } from '@mui/material';
import { GridLayout } from 'shared/ui/grid-layout';
import PriceSpan from 'shared/ui/price-span';
import { SalaryFundDto } from '@sr/dto';
import { formatPersonPruning } from 'utils/person-format-helpers';
import { salaryEntriesApi } from 'entities/salary/salary-entries/salary-entries.api';
import { useSnack } from 'shared/ui/snack';
import { useMemo, useState } from 'react';
import { FormDialog } from 'shared/ui/form-dialog';
import { Field, FormikErrors } from 'formik';
import { formatDateMonthYear } from 'utils/date-format-helpers';
import { CheckboxWithLabel } from 'formik-mui';

const column = ['ФИО сотрудника', 'Кол-во счетов', 'Прибыль по закр.счетам', 'Уже удержано в текущем месяце', 'Еще к удержанию в текущем месяце', 'Всего в фонде риска'];

const getStatusLabel = (user: any): string | null => {
  if (!user) return null;
  if (user.isFired) return 'уволен';
  if (!user.isActive) return 'неактивен';
  return null;
};

export const PremiumFundList = () => {
  const [searchParams] = useSearchParams();
  const { rows, isLoading, error } = usePremiumFund(Number(searchParams.get('year')), Number(searchParams.get('month')), Number(searchParams.get('manager')));

  const visibleRows = useMemo(() => {
    return (rows ?? []).filter((row: SalaryFundDto) => {
      const isFiredOrInactive = Boolean(row.user?.isFired) || !row.user?.isActive;

      if (!isFiredOrInactive) return true;

      const isEmpty =
        !row.countInvoices &&
        !row.countInvoicesClosed &&
        !row.managerTotalProfit &&
        !row.premiumFundSum &&
        !row.premiumFundSumMore &&
        !row.premiumFundTotalSum;

      return !isEmpty;
    });
  }, [rows]);

  return (
    <GridLayout
      columns={column}
      items={visibleRows}
      isLoading={isLoading}
      error={error}
      noItemsText="Список пуст"
      itemRender={(i: any) => (
        <PremiumFundListRow key={`${i.user.id} + ${i.user.firstName}`} row={i} year={Number(searchParams.get('year'))} month={Number(searchParams.get('month'))} />
      )}
    />
  );
};

type RowProps = {
  row: SalaryFundDto;
  year: number;
  month: number;
};

export interface FormValues {
  premiumFund: number[];
}

export const validate = (values: FormValues) => {
  const errors: FormikErrors<FormValues> = {};
  if (values.premiumFund?.length === 0) errors.premiumFund = 'Не возможно выдать пустоту. Выберите накопления';
  return errors;
};

const initialValues: FormValues = {
  premiumFund: []
};

const PremiumFundListRow = ({ row, year, month }: RowProps) => {
  const [showIssuanceDialog, setShowIssuanceDialog] = useState(false);
  const { showError, showSuccess } = useSnack();

  const [addSalaryEntryRetentionInFund] = salaryEntriesApi.useCreatePremiumFundRetentionMutation();
  const [addSalaryEntryIssuanceInFund] = salaryEntriesApi.useCreatePremiumFundIssuanceMutation();

  const statusLabel = getStatusLabel(row.user);
  const isInactive = statusLabel !== null;

  const handleRetentionOfSavings = async () => {
    await addSalaryEntryRetentionInFund({
      year: year,
      month: month,
      user: row.user
    }).unwrap()
      .then(() => {
        showSuccess('Сумма успешно удержана в премиальный фонд');
      })
      .catch((e) => {
        showError('Ошибка удержания в премиальный фонд: ', e);
      });
  };

  const handleIssuanceOfSavings = async (premiumFund: number[]) => {
    await addSalaryEntryIssuanceInFund({
      premiumFund: premiumFund,
      year: year,
      month: month,
      user: row.user
    }).unwrap()
      .then(() => {
        showSuccess('Сумма успешно выдана из премиального фонда');
        setShowIssuanceDialog(false);
      })
      .catch((e) => {
        showError('Ошибка выдачи из премиального фонда: ', e);
      });
  };

  return (
    <>
      <TableRow key={row.user.id}>
        <TableCell>
          <span style={isInactive ? { color: '#9e9e9e' } : undefined}>
            {formatPersonPruning(row.user)}
            {statusLabel && ` (${statusLabel})`}
          </span>
        </TableCell>
        <TableCell>
          {row.countInvoices}\{row.countInvoicesClosed}
        </TableCell>
        <TableCell>
          <PriceSpan price={row.managerTotalProfit} size="small" />
        </TableCell>
        <TableCell>
          <PriceSpan price={row.premiumFundSum} size="small" />
        </TableCell>
        <TableCell sx={{ minWidth: 230, whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 96px', alignItems: 'center', columnGap: 1 }}>
            <Box sx={{ textAlign: 'left' }}>
              <PriceSpan price={row.premiumFundSumMore} size="small" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {!row.payOrderRow && row.premiumFundSumMore > 0 && (
                <Button onClick={handleRetentionOfSavings} variant="contained" size="small">
                  Удержать
                </Button>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ minWidth: 230, whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 96px', alignItems: 'center', columnGap: 1 }}>
            <Box sx={{ textAlign: 'left' }}>
              <PriceSpan price={row.premiumFundTotalSum} size="small" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {!row.baseSalary && row.premiumFundTotalSum > 0 && (
                <Button onClick={() => setShowIssuanceDialog(true)} variant="contained" size="small">
                  Выдать
                </Button>
              )}
            </Box>
            {!row.baseSalary && row.premiumFundTotalSum > 0 && (
              <>
                <FormDialog
                  initialValues={initialValues}
                  renderForm={(formikProps) => (
                    <Box minWidth={450} pb={1}>
                      <Stack spacing={2} direction="column">
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Премиальный фонд за период:</FormLabel>
                          <FormGroup>
                            {row.salaryEntriesInFund.map((key) => (
                              <Field
                                name="premiumFund"
                                type="checkbox"
                                key={key.id}
                                component={CheckboxWithLabel}
                                checked={formikProps.values.premiumFund?.includes(key.id) || false}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const isChecked = e.target.checked;
                                  const currentValues = formikProps.values.premiumFund || [];
                                  const updatedValues = isChecked ? [...currentValues, key.id] : currentValues.filter((id) => id !== key.id);
                                  formikProps.setFieldValue('premiumFund', updatedValues);
                                }}
                                Label={{
                                  label: `${formatDateMonthYear(key.year, key.month)} (${key.sum}р.)`
                                }}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Stack>
                    </Box>
                  )}
                  renderTitle="Выдать из премиального фонда"
                  open={showIssuanceDialog}
                  onClose={() => setShowIssuanceDialog(false)}
                  onSubmit={(values) => handleIssuanceOfSavings(values.premiumFund)}
                  validate={validate}
                  dialogProps={{
                    fullWidth: true
                  }}
                />
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
};